import { useCallback, useEffect, useState } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Bot, FileText, Loader2, Lock, PlayCircle, ShieldCheck } from "lucide-react";
import { api, apiError, inr, logError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SalaryRules, TimeLossReview } from "@/pages/SalaryRules";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const verdictTone = (v) =>
  v === "HIGH RISK" ? "text-destructive" : v === "WARNING" ? "text-amber-600" : "text-emerald-600";

export default function Payroll() {
  const [summary, setSummary] = useState({});
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [month, setMonth] = useState(thisMonth());
  const [busy, setBusy] = useState("");
  const [forecast, setForecast] = useState(null);
  const [period, setPeriod] = useState("3m");
  const [actions, setActions] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(() => {
    api.get("/payroll/summary").then(({ data }) => setSummary(data)).catch((e) => logError("payroll summary", e));
    api.get("/payroll/runs").then(({ data }) => setRuns(data)).catch((e) => logError("payroll runs", e));
    api.get("/hr/actions/pending").then(({ data }) => setActions(data.actions))
      .catch((e) => logError("ai actions", e));
    api.get("/payroll/analytics").then(({ data }) => setAnalytics(data))
      .catch((e) => logError("payroll analytics", e));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get(`/payroll/forecast?period=${period}`).then(({ data }) => setForecast(data))
      .catch((e) => logError("forecast", e));
  }, [period]);

  const act = async (key, fn, message) => {
    setBusy(key);
    try {
      const { data } = await fn();
      if (data?.month || data?.status) setRun(data);
      if (message) toast.success(message);
      load();
      return data;
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const openRun = async (rid) => {
    try {
      const { data } = await api.get(`/payroll/runs/${rid}`);
      setRun(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const audit = run?.audit_result;

  return (
    <div>
      <PageHeader testId="payroll-header" title="Payroll"
        subtitle="Attendance, leave, overtime, incentives and advances become salary — checked by AI, approved by you."
        actions={
          <div className="flex items-center gap-2">
            <Input type="month" className="h-9 w-40" data-testid="payroll-month-input"
              value={month} onChange={(e) => setMonth(e.target.value)} />
            <Button className="gap-2" data-testid="payroll-calculate-button" disabled={busy === "calc"}
              onClick={() => act("calc", () => api.post("/payroll/calculate", { month }), "Payroll calculated")}>
              {busy === "calc" ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              Calculate
            </Button>
          </div>
        } />

      {actions.length > 0 && (
        <div className="border-b border-border bg-secondary/60 px-4 py-3 sm:px-6"
          data-testid="ai-action-bar">
          <div className="overline flex items-center gap-1.5">
            <Bot className="h-3 w-3" /> AI is ready — nothing runs until you confirm
          </div>
          <div className="mt-2 space-y-2">
            {actions.map((a) => (
              <div key={a.code} className="flex flex-wrap items-center gap-2 text-sm"
                data-testid={`ai-action-${a.code}`}>
                <span className="min-w-0 flex-1">
                  <b>{a.title}</b>{" "}
                  <span className="text-muted-foreground">{a.detail}</span>
                </span>
                <Button size="sm" data-testid={`ai-action-confirm-${a.code}`}
                  disabled={busy === a.code}
                  onClick={() => act(a.code, () => api.post(a.endpoint, a.payload || {}),
                    `${a.confirm_label} done`)}>
                  {busy === a.code ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                  {a.confirm_label}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 px-4 py-4 sm:grid-cols-4 sm:px-6">
        <div className="kpi-card" data-testid="payroll-stat-current">
          <div className="overline">This month net</div>
          <div className="mt-2 font-mono-num text-2xl font-bold">
            {inr(summary.current_run?.totals?.net_pay || 0)}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {summary.current_run?.status || "not calculated"}
          </div>
        </div>
        <div className="kpi-card" data-testid="payroll-stat-employees">
          <div className="overline">Employees in run</div>
          <div className="mt-2 font-mono-num text-2xl font-bold">
            {summary.current_run?.totals?.employees ?? 0}
          </div>
        </div>
        <div className="kpi-card" data-testid="payroll-stat-advances">
          <div className="overline">Advance outstanding</div>
          <div className="mt-2 font-mono-num text-2xl font-bold">{inr(summary.advance_outstanding || 0)}</div>
        </div>
        <div className="kpi-card" data-testid="payroll-stat-incentives">
          <div className="overline">Incentives pending</div>
          <div className="mt-2 font-mono-num text-2xl font-bold">{inr(summary.pending_incentives || 0)}</div>
        </div>
      </div>

      <Tabs defaultValue="runs" className="px-4 pb-10 sm:px-6">
        <TabsList>
          <TabsTrigger value="runs" data-testid="payroll-tab-runs">Payroll runs</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="payroll-tab-forecast">AI forecast</TabsTrigger>
          <TabsTrigger value="departments" data-testid="payroll-tab-departments">Departments</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="payroll-tab-analytics">HR analytics</TabsTrigger>
          <TabsTrigger value="rules" data-testid="payroll-tab-rules">Salary rules</TabsTrigger>
          <TabsTrigger value="timeloss" data-testid="payroll-tab-timeloss">Time loss</TabsTrigger>
        </TabsList>

        <TabsContent value="runs">
          <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
            <div data-testid="payroll-run-list">
              {runs.length === 0 && <p className="py-6 text-sm text-muted-foreground">No payroll runs yet.</p>}
              {runs.map((r) => (
                <button key={r.id} type="button" data-testid={`payroll-run-${r.month}`}
                  onClick={() => openRun(r.id)}
                  className={`flex w-full items-center justify-between border-b border-border py-2.5 text-left text-sm ${
                    run?.id === r.id ? "font-semibold text-primary" : ""}`}>
                  <span>{r.month}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono-num text-xs">{inr(r.totals?.net_pay)}</span>
                    <Badge variant={r.status === "Locked" ? "default" : "outline"}>{r.status}</Badge>
                  </span>
                </button>
              ))}
            </div>

            <div>
              {!run ? (
                <p className="py-6 text-sm text-muted-foreground">Pick a run, or calculate the current month.</p>
              ) : (
                <div data-testid="payroll-run-detail">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="font-display text-lg">{run.month}</span>
                    <Badge data-testid="payroll-run-status">{run.status}</Badge>
                    <span className="font-mono-num text-sm">{inr(run.totals?.net_pay)} net</span>
                    <div className="ml-auto flex gap-2">
                      <Button size="sm" variant="outline" className="gap-1.5" data-testid="payroll-audit-button"
                        disabled={busy === "audit"}
                        onClick={() => act("audit", async () => {
                          const { data } = await api.post(`/payroll/runs/${run.id}/ai-audit`);
                          const fresh = await api.get(`/payroll/runs/${run.id}`);
                          setRun(fresh.data);
                          return { data: fresh.data };
                        }, "AI payroll check complete")}>
                        {busy === "audit" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />}
                        AI check
                      </Button>
                      {run.status === "Draft" && (
                        <Button size="sm" className="gap-1.5" data-testid="payroll-approve-button"
                          disabled={busy === "approve"}
                          onClick={() => act("approve", () => api.post(`/payroll/runs/${run.id}/approve`), "Payroll approved")}>
                          <ShieldCheck className="h-3.5 w-3.5" /> Approve
                        </Button>
                      )}
                      {run.status === "Approved" && (
                        <Button size="sm" className="gap-1.5" data-testid="payroll-lock-button"
                          disabled={busy === "lock"}
                          onClick={() => act("lock", () => api.post(`/payroll/runs/${run.id}/lock`),
                            "Payroll locked and payslips generated")}>
                          <Lock className="h-3.5 w-3.5" /> Lock & generate payslips
                        </Button>
                      )}
                    </div>
                  </div>

                  {audit && (
                    <div className="mb-4 rounded-lg border border-border bg-card p-3" data-testid="payroll-audit-result">
                      <div className={`text-sm font-bold ${verdictTone(audit.verdict)}`} data-testid="payroll-audit-verdict">
                        AI verdict: {audit.verdict} • {audit.high_risk} high risk • {audit.warnings} warning(s)
                      </div>
                      {audit.ai_summary && <p className="mt-1 text-xs text-muted-foreground">{audit.ai_summary}</p>}
                      {audit.findings.slice(0, 8).map((f) => (
                        <div key={f.user_id} className="mt-1 text-xs">
                          <b>{f.user_name}</b> <span className={verdictTone(f.level)}>{f.level}</span>
                          {" — "}{f.notes.join("; ")}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="payroll-lines-table">
                      <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="py-2">Employee</th>
                          <th className="py-2 text-right">Days</th>
                          <th className="py-2 text-right">Gross earned</th>
                          <th className="py-2 text-right">OT</th>
                          <th className="py-2 text-right">Incentive</th>
                          <th className="py-2 text-right">Deductions</th>
                          <th className="py-2 text-right">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(run.lines || []).map((l) => (
                          <tr key={l.user_id} className="border-t border-border" data-testid={`payroll-line-${l.user_id}`}>
                            <td className="py-2">
                              {l.user_name}
                              <span className="block text-[10px] text-muted-foreground">{l.department || "—"}</span>
                            </td>
                            <td className="py-2 text-right font-mono-num text-xs">
                              {l.attendance.payable_days}/{l.attendance.working_days_basis}
                            </td>
                            <td className="py-2 text-right font-mono-num">{inr(l.earnings.earned_gross)}</td>
                            <td className="py-2 text-right font-mono-num">{inr(l.earnings.overtime)}</td>
                            <td className="py-2 text-right font-mono-num">{inr(l.earnings.incentives)}</td>
                            <td className="py-2 text-right font-mono-num text-destructive">-{inr(l.deductions.total)}</td>
                            <td className="py-2 text-right font-mono-num font-bold">{inr(l.net_pay)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {run.status === "Locked" && (
                    <Button size="sm" variant="outline" className="mt-3 gap-1.5" data-testid="payroll-payslips-link"
                      onClick={async () => {
                        const { data } = await api.get(`/payroll/payslips?month=${run.month}`);
                        toast.success(`${data.length} payslip(s) generated for ${run.month}`);
                      }}>
                      <FileText className="h-3.5 w-3.5" /> Check payslips
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="forecast" data-testid="payroll-forecast">
          <div className="mb-3 flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32" data-testid="forecast-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[["7d", "7 days"], ["1m", "1 month"], ["3m", "3 months"], ["6m", "6 months"],
                  ["1y", "1 year"], ["5y", "5 years"]].map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {forecast && (
              <span className="text-sm">
                Expected liability{" "}
                <b className="font-mono-num" data-testid="forecast-total">{inr(forecast.total_liability)}</b>
              </span>
            )}
          </div>
          {forecast && (
            <>
              <p className="mb-3 max-w-2xl text-xs text-muted-foreground" data-testid="forecast-explanation">
                {forecast.explanation}
              </p>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                  <AreaChart data={forecast.projection.map((p) => ({ name: `+${p.period}`, net: p.net }))}>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => inr(v)} />
                    <Area type="monotone" dataKey="net" stroke="hsl(var(--accent))"
                      fill="hsl(var(--accent) / 0.2)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="analytics" data-testid="payroll-analytics">
          {!analytics ? (
            <p className="py-6 text-sm text-muted-foreground">Loading analytics…</p>
          ) : (
            <div className="space-y-5">
              {analytics.alerts.length > 0 && (
                <div className="rounded-lg border border-border bg-card p-3" data-testid="hr-ai-alerts">
                  <div className="overline mb-1 flex items-center gap-1.5">
                    <Bot className="h-3 w-3" /> AI alerts
                  </div>
                  {analytics.alerts.map((a) => (
                    <div key={a} className="text-xs text-muted-foreground">• {a}</div>
                  ))}
                </div>
              )}
              <div className="grid gap-4 lg:grid-cols-2">
                {[["Payroll cost trend", analytics.cost_trend, "net", "month", true],
                  ["Overtime hours trend", analytics.overtime_trend, "hours", "month", false],
                  ["Approved leave days", analytics.leave_trend, "days", "month", false],
                  ["Salary distribution", analytics.salary_spread, "employees", "band", false]]
                  .map(([title, rows, key, label, money]) => (
                  <div key={title} className="rounded-lg border border-border bg-card p-3">
                    <div className="overline mb-2">{title}</div>
                    {(rows || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground">Not enough data yet.</p>
                    ) : (
                      <div className="h-40 w-full" data-testid={`chart-${key}`}>
                        <ResponsiveContainer width="100%" height="100%" minHeight={140}>
                          <AreaChart data={rows}>
                            <XAxis dataKey={label} tick={{ fontSize: 9 }} />
                            <Tooltip formatter={(v) => (money ? inr(v) : v)} />
                            <Area type="monotone" dataKey={key} stroke="hsl(var(--accent))"
                              fill="hsl(var(--accent) / 0.2)" strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="departments" data-testid="payroll-departments">          {(summary.departments || []).length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">Approve a payroll run to see department cost.</p>
          ) : (
            summary.departments.map((d) => (
              <div key={d.department} className="flex justify-between border-b border-border py-2 text-sm">
                <span>{d.department} <span className="text-xs text-muted-foreground">({d.employees})</span></span>
                <span className="font-mono-num">{inr(d.net)}</span>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="rules" className="mt-4">
          <SalaryRules />
        </TabsContent>

        <TabsContent value="timeloss" className="mt-4">
          <TimeLossReview />
        </TabsContent>
      </Tabs>
    </div>
  );
}
